import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { Typography, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, Paper, Box, Button, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Modal, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material'; // Added back
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Field, Form } from 'formik';
import { FormHelperText } from '@material-ui/core';
import * as Constants from "constants/index";
import { familyCompositionValidationSchema } from 'validations/validationFamilyComposition';
import { notification } from "antd";
import InstructionText from '../shared/InstructionText';

function FamilyComposition({ handleNext, handleBack, information, onInformationChange, personalInformation, onPersonalInformationChange }) {

  const civilStatusOptions = Constants.civilStatusOptions;
  const [formData, setFormData] = useState({
    name: '',
    birthday: null,
    gender: '',
    relationshipToChild: '',
    civilStatus: '',
    educationalAttainment: '',
    work: '',
    workPlace: '',
    salaryPerMonth: '',
    OtherInterests: '',
    pwdId: '',
    illness: '',
    philhealthNumber: '',
    vaccineStatus: ''
  });
  const [rows, setRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  // Convert any string dates in initial data to proper Date objects
  useEffect(() => {
    if (information && Array.isArray(information)) {
      const processedRows = information.map(row => {
        if (row.birthday && typeof row.birthday === 'string') {
          const parsedDate = dayjs(row.birthday, ['MM-DD-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'], true);
          return {
            ...row,
            birthday: parsedDate.isValid() ? parsedDate.toDate() : null
          };
        }
        return row;
      });
      setRows(processedRows);
    } else {
      setRows(information || []);
    }
  }, [information]);

  // Synchronize first row with personal information when it changes
  useEffect(() => {
    if (personalInformation && rows.length > 0) {
      const data = personalInformation;
      const formattedDate = data.parentBirthday ? dayjs(data.parentBirthday).toDate() : null;
      
      // Build the full parent name
      const nameParts = [data.parentFirstName, data.parentMiddleName, data.parentSurname, data.parentExtension].filter(Boolean);
      const fullName = nameParts.join(' ');
      
      // Only update if there are actual changes to avoid infinite loops
      const currentFirstRow = rows[0];
      const shouldUpdate = 
        currentFirstRow.name !== fullName ||
        (currentFirstRow.birthday && formattedDate && new Date(currentFirstRow.birthday).getTime() !== formattedDate.getTime()) ||
        currentFirstRow.gender !== data.parentGender ||
        currentFirstRow.civilStatus !== data.parentCivilStatus ||
        currentFirstRow.educationalAttainment !== (data.parentEducationalAttainment === "IbaPa" ? data.parentSpecificEducationalAttainment : data.parentEducationalAttainment) ||
        currentFirstRow.vaccineStatus !== (data.parentVaccineStatus === "Oo" ? data.parentVaccineShotCount : "None");
      
      if (shouldUpdate) {
        const updatedFirstRow = {
          ...currentFirstRow,
          name: fullName,
          birthday: formattedDate,
          gender: data.parentGender,
          relationshipToChild: "Magulang/Guardian",
          civilStatus: data.parentCivilStatus,
          educationalAttainment: data.parentEducationalAttainment === "IbaPa" ? data.parentSpecificEducationalAttainment : data.parentEducationalAttainment,
          vaccineStatus: data.parentVaccineStatus === "Oo" ? data.parentVaccineShotCount : "None",
        };
        
        const updatedRows = [updatedFirstRow, ...rows.slice(1)];
        setRows(updatedRows);
        onInformationChange(updatedRows);
      }
    }
  }, [personalInformation, rows.length]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    handleClearForm();
  };

  const handleOpenEdit = (row, index) => {
    
    // Convert birthday to proper Date object if it's a string
    let birthdayValue = row.birthday;
    if (birthdayValue && typeof birthdayValue === 'string') {
      // Handle different date formats consistently across platforms
      const parsedDate = dayjs(birthdayValue, ['MM-DD-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'], true);
      birthdayValue = parsedDate.isValid() ? parsedDate.toDate() : null;
    }
    
    setFormData({
      name: row.name,
      birthday: birthdayValue,
      gender: row.gender,
      relationshipToChild: row.relationshipToChild,
      civilStatus: row.civilStatus,
      educationalAttainment: row.educationalAttainment,
      work: row.work,
      workPlace: row.workPlace,
      salaryPerMonth: row.salaryPerMonth,
      OtherInterests: row.OtherInterests,
      pwdId: row.pwdId,
      illness: row.illness,
      philhealthNumber: row.philhealthNumber,
      vaccineStatus: row.vaccineStatus
    })
    setEditingRow(row);
    setEditingIndex(index);
    setModalOpen(true);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#1c5272",
      color: theme.palette.common.white,
      fontWeight: "bold",
      padding: "8px",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const columns = [
    "Pangalan ng Miyembro",
    "Kaarawan",
    "Kasarian",
    "Relasyon sa Bata",
    "Civil Status",
    "Educational Attainment at pangalan ng paaralan",
    "Trabaho at Pangalan ng pinagtratrabahuan",
    "Lugar ng Trabaho",
    "Kinikita ng Miyembro kada Buwan",
    "Ibang Pinagkaabalahan",
    "PWD ID Number",
    "Illness/Disability",
    "Philhealth Number",
    "May bakuna para sa COVID-19 at pang-ilang shot?",
    "Actions",
  ];

  const handleClearForm = () => {
    setFormData({
      name: '',
      birthday: null,
      gender: '',
      relationshipToChild: '',
      civilStatus: '',
      educationalAttainment: '',
      work: '',
      workPlace: '',
      salaryPerMonth: '',
      OtherInterests: '',
      pwdId: '',
      illness: '',
      philhealthNumber: '',
      vaccineStatus: ''
    });
    setEditingRow(null);
    setEditingIndex(-1);
    setModalOpen(false);
  };

  /**
   * Adds a new family member to the rows array.
   *
   * @param {Object} values - The values of the new family member.
   */
  const handleAddFamily = (values) => {
    setRows(prevRows => {
      const updatedRows = [...prevRows, values];
      onInformationChange(updatedRows); // Ensure onInformationChange is called
      return updatedRows;
    });
    handleClearForm(); // Added as per plan 3.3
  };

  /**
   * Edits the details of an existing family member.
   *
   * @param {Object} values - The new values for the family member.
   * @param {number} index - The index of the family member to be edited.
   */
  const handleEditFamily = (values, index) => {
    
    setRows(prevRows => {
      const updatedRows = prevRows.map((row, i) => (i === index ? values : row));
      onInformationChange(updatedRows); // Ensure onInformationChange is called
      
      // If editing the first row (parent/guardian), sync with personal information
      if (index === 0 && onPersonalInformationChange && personalInformation) {
        
        // Parse the full name into components
        const nameParts = values.name.trim().split(' ').filter(Boolean);
        let firstName = '', middleName = '', surname = '', extension = '';
        
        if (nameParts.length >= 1) firstName = nameParts[0];
        if (nameParts.length >= 2) surname = nameParts[nameParts.length - 1];
        if (nameParts.length >= 3) {
          // Check if last part is an extension (Jr., Sr., III, etc.)
          const lastPart = nameParts[nameParts.length - 1];
          const extensions = ['Jr.', 'Sr.', 'III', 'IV', 'V', 'Jr', 'Sr'];
          if (extensions.includes(lastPart)) {
            extension = lastPart;
            surname = nameParts[nameParts.length - 2];
            if (nameParts.length >= 4) {
              middleName = nameParts.slice(1, -2).join(' ');
            }
          } else {
            middleName = nameParts.slice(1, -1).join(' ');
          }
        }
        
        // Parse birthday to dayjs format for consistency
        const formattedBirthday = values.birthday ? dayjs(values.birthday) : null;
        
        const updatedPersonalInfo = {
          ...personalInformation,
          parentFirstName: firstName,
          parentMiddleName: middleName,
          parentSurname: surname,
          parentExtension: extension,
          parentBirthday: formattedBirthday,
          parentGender: values.gender,
          parentCivilStatus: values.civilStatus,
          parentEducationalAttainment: values.educationalAttainment,
          parentVaccineStatus: values.vaccineStatus !== 'None' ? 'Oo' : 'Wala',
          parentVaccineShotCount: values.vaccineStatus !== 'None' ? values.vaccineStatus : ''
        };
        
        onPersonalInformationChange(updatedPersonalInfo);
      }
      
      return updatedRows;
    });
    handleClearForm(); // Added as per plan 3.3
  };

  /**
   * Deletes a family member from the rows array.
   *
   * @param {Object} rowToDelete - The row representing the family member to be deleted.
   */
  const handleDelete = (rowToDelete) => {
    const rowIndex = rows.indexOf(rowToDelete);
    
    // Prevent deletion of the first row (parent/guardian)
    if (rowIndex === 0) {
      notification["warning"]({
        message: "Cannot Delete",
        description: "The parent/guardian information cannot be deleted. You can edit it instead.",
        duration: 4,
      });
      return;
    }
    
    if (window.confirm("Are you sure you want to delete this item?")) {
      // Filters out the row to be deleted from the rows array and updates the rows state.
      const newRows = rows.filter(row => row !== rowToDelete);
      setRows(newRows);
      onInformationChange(newRows);
    } else {
      // If the user cancels the deletion, an alert is shown.
      alert("Deletion cancelled");
    }
  };

  const handleClickNext = async () => {
    if (isNextLoading) return; // Prevent double clicks
    
    if (rows.length < 1) {
      notification["error"]({
        message: "Form Incomplete",
        description: "Please fill in all required fields and correct any errors before proceeding.",
        duration: 4,
      });
      
      // Scroll to the Add Member button - center vertically in viewport
      const addButton = document.querySelector('[data-testid="add-family-member-button"]');
      if (addButton) {
        const elementRect = addButton.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
        window.scrollTo({ top: middle, behavior: 'smooth' });
      }
      
      return;
    }
  
    // Validate all family members using the Yup validation schema
    try {
      setIsNextLoading(true);
      
      // Validate each family member against the schema
      for (let i = 0; i < rows.length; i++) {
        try {
          await familyCompositionValidationSchema.validate(rows[i], { abortEarly: false });
        } catch (validationError) {
          // Extract field-specific errors
          const errorMessages = validationError.inner.map(err => err.message).join(', ');
          
          notification["error"]({
            message: "Form Incomplete",
            description: "Please fill in all required fields and correct any errors before proceeding.",
            duration: 4,
          });
          
          // Scroll to the table to show the error - center vertically in viewport
          const tableElement = document.querySelector('table');
          if (tableElement) {
            const elementRect = tableElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
            window.scrollTo({ top: middle, behavior: 'smooth' });
          }
          
          setIsNextLoading(false);
          return;
        }
      }
      
      // If all validations pass, proceed to next step
      onInformationChange(rows);
      await handleNext();
    } catch (error) {
      console.error('Error proceeding to next step:', error);
      notification.error({
        message: 'Error',
        description: 'An error occurred while saving. Please try again.',
        duration: 4,
      });
    } finally {
      setIsNextLoading(false);
    }
  }

  const handleChange = (e, setFieldValue) => {
    const value = e.target.value;
    
    // Allow empty strings (for clearing the field)
    if (value === '') {
      setFieldValue(e.target.name, value);
      return;
    }
    
    // More permissive regex for names - allow letters, spaces, periods, hyphens, apostrophes
    // Include Filipino characters (ñ, Ñ) and other common name characters
    // Don't allow starting with space, but allow everything else
    const regex = /^[a-zA-ZñÑ][a-zA-ZñÑ\s\.\-']*$/;

    if (regex.test(value)) {
      setFieldValue(e.target.name, value);
    }
    // If regex fails, we simply don't update the field (silent validation)
  }

  const handleChangeNumbers = (e, setFieldValue) => {
    const value = e.target.value;
    // Regular expression to allow only digits (0-9)
    const regex = /^[0-9]*$/;

    // Only allow the input if it is a number and does not include e or +
    if (regex.test(value)) {
      setFieldValue(e.target.name, value);
    }
  };

  /**
   * Format date for display in a consistent, cross-platform way
   * @param {Date|string} date - The date to format
   * @returns {string} Formatted date string (MM/DD/YYYY)
   */
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    
    try {
      // Handle both Date objects and string dates
      const dateObj = date instanceof Date ? date : new Date(date);
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) return '';
      
      // Format as MM/DD/YYYY for consistent display
      return dayjs(dateObj).format('MM/DD/YYYY');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  return (
    <Box mt={2} sx={{ px: { xs: 1, sm: 0 } }}>
      {/* Header section */}
      <Typography 
        variant="subtitle1" 
        fontWeight="bold"
        sx={{
          fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
          mb: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 0 }
        }}
      >
        I. Ilagay ang mga kapamilyang kasama natin sa bahay. Hindi na kailangan
        isulat ang batang pinapasok natin sa Kabahagi
      </Typography>

      {/* Add Family Member Button */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenModal}
          startIcon={<AddIcon />}
          aria-label="Add Family Member"
          data-testid="add-family-member-button"
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Add Family Member
        </Button>
      </Box>
      
      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        {rows.length > 0 ? (
          <Box>
            {rows?.map((row, index) => (
              <Paper 
                key={`${row.name}-${index}`}
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="primary" mb={1.5}>
                  {row.name}
                  {index === 0 && (
                    <Typography 
                      component="span" 
                      variant="caption" 
                      sx={{ 
                        ml: 1, 
                        px: 1, 
                        py: 0.5, 
                        backgroundColor: '#1976d2', 
                        color: 'white', 
                        borderRadius: 1,
                        fontSize: '0.7rem'
                      }}
                    >
                      Parent/Guardian
                    </Typography>
                  )}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Kaarawan:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {formatDateForDisplay(row.birthday)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Kasarian:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {row.gender}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Relasyon sa Bata:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {row.relationshipToChild}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Civil Status:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {row.civilStatus}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Kinikita:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      ₱{row.salaryPerMonth}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Trabaho:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {row.work}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Educational Attainment:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {row.educationalAttainment}
                    </Typography>
                  </Grid>
                  {row.illness && row.illness !== 'N/A' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Illness/Disability:
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {row.illness}
                      </Typography>
                    </Grid>
                  )}
                  {row.vaccineStatus && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Vaccine Status:
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {row.vaccineStatus}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenEdit(row, index)}
                    size="small"
                    sx={{ flex: 1 }}
                    aria-label={`Edit${index}`}
                    data-testid={`Edit${index}`}
                  >
                    <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleDelete(row)}
                    size="small"
                    sx={{ flex: 1 }}
                    aria-label={`Delete${index}`}
                    data-testid={`Delete${index}`}
                    disabled={index === 0}
                    title={index === 0 ? "Parent/Guardian cannot be deleted" : "Delete family member"}
                  >
                    <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Delete
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              No family members added yet. Click "Add Family Member" to get started.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Desktop Table View */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          display: { xs: 'none', lg: 'block' },
          overflowX: 'auto'
        }}
      >
        {rows.length > 0 ? (
          <Table sx={{ minWidth: 650 }} aria-label="family members table">
            <TableHead>
              <TableRow>
                {columns?.map((column, index) => (
                  <StyledTableCell key={index} align="center">
                    {column}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row, index) => (
                <TableRow
                  key={`${row.name}-${index}`}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="center">
                    {row.name}
                    {index === 0 && (
                      <Typography 
                        component="div" 
                        variant="caption" 
                        sx={{ 
                          mt: 0.5,
                          px: 1, 
                          py: 0.25, 
                          backgroundColor: '#1976d2', 
                          color: 'white', 
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          display: 'inline-block'
                        }}
                      >
                        Parent/Guardian
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{formatDateForDisplay(row.birthday)}</TableCell>
                  <TableCell align="center">{row.gender}</TableCell>
                  <TableCell align="center">{row.relationshipToChild}</TableCell>
                  <TableCell align="center">{row.civilStatus}</TableCell>
                  <TableCell align="center">{row.educationalAttainment}</TableCell>
                  <TableCell align="center">{row.work}</TableCell>
                  <TableCell align="center">{row.workPlace}</TableCell>
                  <TableCell align="center">{row.salaryPerMonth}</TableCell>
                  <TableCell align="center">{row.OtherInterests}</TableCell>
                  <TableCell align="center">{row.pwdId}</TableCell>
                  <TableCell align="center">{row.illness}</TableCell>
                  <TableCell align="center">{row.philhealthNumber}</TableCell>
                  <TableCell align="center">{row.vaccineStatus}</TableCell>
                  <TableCell align="center">
                    <Box mb={1}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(row)}
                        fullWidth
                        aria-label={`Delete${index}`}
                        data-testid={`Delete${index}`}
                        disabled={index === 0}
                        title={index === 0 ? "Parent/Guardian cannot be deleted" : "Delete family member"}
                      >
                        <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Delete
                      </Button>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleOpenEdit(row, index)}
                      fullWidth
                      aria-label={`Edit${index}`}
                      data-testid={`Edit${index}`}
                    >
                      <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              No family members added yet. Click "Add Family Member" to get started.
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Modal for Add/Edit Family Member */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="family-modal-title"
        aria-describedby="family-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
          maxWidth: '800px',
          maxHeight: { xs: '90vh', sm: '85vh' },
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          borderRadius: 2,
          overflow: 'auto'
        }}>
          <Formik
            initialValues={formData}
            validationSchema={familyCompositionValidationSchema}
            onSubmit={(values) => {
              if (editingIndex >= 0) {
                handleEditFamily(values, editingIndex);
              } else {
                handleAddFamily(values);
              }
            }}
            enableReinitialize
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Box sx={{
                  p: { xs: 2, sm: 3 }, 
                  bgcolor: 'background.paper'
                }}>
                  <Typography 
                    id="family-modal-title" 
                    variant="h6" 
                    component="h2" 
                    align="center" 
                    mb={{ xs: 3, sm: 4 }} 
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    {editingIndex >= 0 ? 'Edit Family Member Details' : 'Magdagdag ng kapamilyang kasama natin sa bahay'}
                  </Typography>
                  
                  {editingIndex === 0 && (
                    <Box 
                      sx={{ 
                        mb: 3, 
                        p: 2, 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: 1, 
                        border: '1px solid #1976d2' 
                      }}
                    >
                      <Typography variant="body2" color="primary" fontWeight="bold" align="center">
                        📝 You are editing the Parent/Guardian information. Changes here will also update the personal information in Step 1.
                      </Typography>
                    </Box>
                  )}
                  
                  <InstructionText />
                  
                  {/* Personal Information Section */}
                  <fieldset style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', margin: '0 0 24px 0' }}>
                    <legend style={{ padding: '0 8px', fontWeight: 'bold', color: '#1976d2' }}>
                      Personal Information
                    </legend>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '6px'}}>
                        <Field
                          as={TextField}
                          name="name"
                          label={<>Pangalan ng Miyembro <span style={{color: 'red'}}>*</span></>}
                          size="small"
                          fullWidth
                          placeholder="Hal. Juan Dela Cruz"
                          helperText={touched.name && errors.name}
                          error={errors.name && touched.name}
                          aria-label="Family Member Name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '6px'}}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            name="birthday"
                            label={<>Kaarawan <span style={{color: 'red'}}>*</span></>}
                            value={values.birthday ? dayjs(values.birthday) : null}
                            onChange={(value) => {
                              // Store as a proper Date object for consistent cross-platform validation
                              const dateValue = value ? value.toDate() : null;
                              setFieldValue('birthday', dateValue)
                            }}
                            maxDate={dayjs()}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true, 
                                size: 'small',
                                helperText: touched.birthday && errors.birthday,
                                error: Boolean(errors.birthday && touched.birthday)
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '6px'}}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Kasarian <span style={{color: 'red'}}>*</span></InputLabel>
                          <Field
                            as={Select}
                            name="gender"
                            label={<>Kasarian <span style={{color: 'red'}}>*</span></>}
                            helperText={touched.gender && errors.gender}
                            error={touched.gender && errors.gender}
                            aria-label="Gender"
                          >
                            <MenuItem value="Male">Lalaki</MenuItem>
                            <MenuItem value="Female">Babae</MenuItem>
                          </Field>
                          <FormHelperText>{touched.gender && errors.gender}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '6px'}}>
                        <Field
                          as={TextField}
                          name="relationshipToChild"
                          label={<>Relasyon sa Bata <span style={{color: 'red'}}>*</span></>}
                          size="small"
                          fullWidth
                          placeholder="Hal. Anak, Tatay o Kapatid"
                          helperText={touched.relationshipToChild && errors.relationshipToChild}
                          error={errors.relationshipToChild && touched.relationshipToChild}
                          onChange={(e) => handleChange(e, setFieldValue)}
                          value={editingIndex === 0 ? "Magulang/Guardian" : values.relationshipToChild}
                          InputProps={{
                            readOnly: editingIndex === 0,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </fieldset>

                  {/* Civil Status & Education Section */}
                  <fieldset style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', margin: '0 0 24px 0' }}>
                    <legend style={{ padding: '0 8px', fontWeight: 'bold', color: '#1976d2' }}>
                      Civil Status & Education
                    </legend>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Civil Status <span style={{color: 'red'}}>*</span></InputLabel>
                          <Field
                            as={Select}
                            name="civilStatus"
                            label={<>Civil Status <span style={{color: 'red'}}>*</span></>}
                            helperText={touched.civilStatus && errors.civilStatus}
                            error={touched.civilStatus && errors.civilStatus}
                          >
                            {civilStatusOptions.map(status => (
                              <MenuItem key={status.value} value={status.value}>
                                {status.label}
                              </MenuItem>
                            ))}
                          </Field>
                          <FormHelperText>{touched.civilStatus && errors.civilStatus}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="educationalAttainment"
                          label={<>Educational Attainment <span style={{color: 'red'}}>*</span></>}
                          size="small"
                          fullWidth
                          placeholder="Elementary, Highschool, College, Vocational, Iba pa"
                          helperText={touched.educationalAttainment && errors.educationalAttainment}
                          error={errors.educationalAttainment && touched.educationalAttainment}
                          onChange={(e) => handleChange(e, setFieldValue)}
                        />
                      </Grid>
                    </Grid>
                  </fieldset>

                  {/* Work Information Section */}
                  <fieldset style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', margin: '0 0 24px 0' }}>
                    <legend style={{ padding: '0 8px', fontWeight: 'bold', color: '#1976d2' }}>
                      Work Information
                    </legend>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="work"
                          label="Trabaho"
                          size="small"
                          fullWidth
                          placeholder="Hal. Driver, Jollibee"
                          helperText={touched.work && errors.work}
                          error={false}
                          onChange={(e) => handleChange(e, setFieldValue)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="workPlace"
                          label="Lugar ng Trabaho"
                          size="small"
                          fullWidth
                          placeholder="Hal. Mandaluyong City"
                          helperText={touched.workPlace && errors.workPlace}
                          error={false}
                          onChange={(e) => handleChange(e, setFieldValue)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="salaryPerMonth"
                          label={<>Kinikita kada Buwan <span style={{color: 'red'}}>*</span></>}
                          size="small"
                          type="number"
                          fullWidth
                          placeholder="Hal. 5,000"
                          helperText={touched.salaryPerMonth && errors.salaryPerMonth}
                          error={errors.salaryPerMonth && touched.salaryPerMonth}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={8} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="OtherInterests"
                          label="Ibang Pinagkaabalahan"
                          size="small"
                          fullWidth
                          placeholder="Hal. Nagtitinda"
                          helperText={touched.OtherInterests && errors.OtherInterests}
                          error={errors.OtherInterests && touched.OtherInterests}
                          onChange={(e) => handleChange(e, setFieldValue)}
                        />
                      </Grid>
                    </Grid>
                  </fieldset>

                  {/* Health & Social Information Section */}
                  <fieldset style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px', margin: '0 0 24px 0' }}>
                    <legend style={{ padding: '0 8px', fontWeight: 'bold', color: '#1976d2' }}>
                      Health & Social Information
                    </legend>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="pwdId"
                          label="PWD ID Number"
                          size="small"
                          fullWidth
                          placeholder="Hal. 12345 - 12345"
                          helperText={touched.pwdId && errors.pwdId}
                          error={errors.pwdId && touched.pwdId}
                          onChange={(e) => handleChangeNumbers(e, setFieldValue)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="illness"
                          label="Illness/Disability"
                          size="small"
                          fullWidth
                          placeholder="Hal. Diabetes, Hypertension"
                          helperText={touched.illness && errors.illness}
                          error={errors.illness && touched.illness}
                          onChange={(e) => handleChange(e, setFieldValue)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <Field
                          as={TextField}
                          name="philhealthNumber"
                          label={<>Philhealth Number <span style={{color: 'red'}}>*</span></>}
                          size="small"
                          fullWidth
                          placeholder="Hal. 123456789101"
                          helperText={touched.philhealthNumber && errors.philhealthNumber}
                          error={errors.philhealthNumber && touched.philhealthNumber}
                          onChange={(e) => handleChangeNumbers(e, setFieldValue)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6} style={{ padding: '5px' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Vaccine Status <span style={{color: 'red'}}>*</span></InputLabel>
                          <Field
                            as={Select}
                            name="vaccineStatus"
                            label={<>Vaccine Status <span style={{color: 'red'}}>*</span></>}
                            size="small"
                            fullWidth
                            placeholder="Hal. Oo - 2"
                            helperText={touched.vaccineStatus && errors.vaccineStatus}
                            error={errors.vaccineStatus && touched.vaccineStatus}
                          >
                            <MenuItem value="None">Wala</MenuItem>
                            <MenuItem value="1st Dose">1st Dose</MenuItem>
                            <MenuItem value="2nd Dose">2nd Dose</MenuItem>
                            <MenuItem value="2nd Dose + Booster">2nd Dose + Booster</MenuItem>
                          </Field>
                          <FormHelperText>{touched.vaccineStatus && errors.vaccineStatus}</FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </fieldset>
                  
                  {/* Action Buttons */}
                  <Box 
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'end',
                      gap: { xs: 1, sm: 0 },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      mt: 4,
                      pt: 3,
                      borderTop: '1px solid #e0e0e0'
                    }}
                  >
                    <Button
                      variant="contained"
                      style={{ backgroundColor: 'grey', color: 'white' }}
                      onClick={handleCloseModal}
                      sx={{ 
                        mr: { xs: 0, sm: 1 },
                        order: { xs: 2, sm: 1 }
                      }}
                      aria-label="Cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      aria-label={editingIndex >= 0 ? "Update Member" : "Add Member"}
                      data-testid="submit-family-member-button"
                      sx={{ order: { xs: 1, sm: 2 } }}
                    >
                      {editingIndex >= 0 ? 'Update Member' : 'Add Member'}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* Navigation buttons */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: 2,
        gap: 2,
        px: { xs: 1, sm: 0 }
      }}>
        <Button
          color="inherit"
          onClick={() => {
            onInformationChange(rows);
            handleBack();
          }}
          variant="outlined"
          sx={{ 
            width: { xs: 'auto', sm: 'auto' }
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleClickNext}
          disabled={isNextLoading}
          variant="contained"
          color="primary"
          sx={{ 
            width: { xs: 'auto', sm: 'auto' }
          }}
        >
          {isNextLoading ? 'Processing...' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}

export default FamilyComposition;
